import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { ChatwootWidget } from '@neumind/shared/react';
import { PostHogProvider, CONSENT_KEY } from '../components/widgets/PostHogProvider';
import { CookieConsent } from '../components/widgets/CookieConsent';
import { CurrencyProvider } from '../contexts/CurrencyContext';
import { NotFoundPage } from './404';

const CHATWOOT_TOKEN = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN;

function ScrollToTop() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function PageViewTracker() {
  const location = useRouterState({ select: (s) => s.location });

  useEffect(() => {
    // Track page views on route changes (PostHog captures initial load automatically)
    // Use a small delay to ensure the page title has updated
    const timeoutId = setTimeout(() => {
      window.posthog?.capture('$pageview', {
        $current_url: window.location.href,
        $pathname: location.pathname,
        $host: window.location.host,
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  return null;
}

function ConditionalChatwoot() {
  const [consentResolved, setConsentResolved] = useState(() => {
    if (typeof window === 'undefined') return false;
    const consent = localStorage.getItem(CONSENT_KEY);
    return consent === 'accepted' || consent === 'declined';
  });

  useEffect(() => {
    const handleStorage = () => {
      const consent = localStorage.getItem(CONSENT_KEY);
      if (consent === 'accepted' || consent === 'declined') {
        setConsentResolved(true);
      }
    };

    window.addEventListener('storage', handleStorage);

    // Also poll briefly in case storage event doesn't fire for same-window changes
    const interval = setInterval(() => {
      const consent = localStorage.getItem(CONSENT_KEY);
      if (consent === 'accepted' || consent === 'declined') {
        setConsentResolved(true);
        clearInterval(interval);
      }
    }, 500);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  if (!CHATWOOT_TOKEN || !consentResolved) return null;

  return <ChatwootWidget websiteToken={CHATWOOT_TOKEN} />;
}

export const Route = createRootRoute({
  notFoundComponent: NotFoundPage,
  component: () => (
    <PostHogProvider>
      <CurrencyProvider>
        <ScrollToTop />
        <PageViewTracker />
        <Layout>
          <Outlet />
        </Layout>
        <ConditionalChatwoot />
        <CookieConsent />
      </CurrencyProvider>
    </PostHogProvider>
  ),
});
